import _ from '@lodash';
import SecurityStatusIcon from 'app/components/SecurityStatusIcon';
import { getLocalTime, getDefaultTimezone, getCSVReportTime } from './dates.helpers';

export const dateRangeOptions = ['days30', 'days7', 'yesterday', 'today', 'custom'] as const;
export const exportReportFormatOptions = ['csv'] as const;
export const exportReportFrequencyOptions = ['once', 'week', 'month', 'year'] as const;

export type ReportEvent = {
	id: string;
	[key: string]: string | number;
};

// DEV NOTE::to add a new report you must:
// * Add it to `reportTypeOptions` (name should match the endpoint)
// * Add it to the `report:key:type:xyz` translations

// DEV NOTE::to add new columns you a report table you must:
// * Add it to the `report:key:column:xyz` translations

// DEV NOTE::additional translations to look out for:
// * `report:key:type:xyz` - for report names
// * `report:key:label:xyz` - for report bar labels (if they're strings like "User" and not values like a device name)
// * `report:key:aggregate:xyz` - for any report aggregate data

export const reportCategoryOptions = ['r2m', 'bsn'] as const;
export const reportTypeOptions = [
	'r2m-executive',
	'r2m-users',
	'r2m-devices',
	'r2m-print-jobs',
	'r2m-unreleased-print-jobs',
	'r2m-expired-print-jobs',
	'r2m-system-print-queue',
	'bsn-device-events',
] as const;

export type ReportType = (typeof reportTypeOptions)[number];

export const getReportTypesByCategory = (category: (typeof reportCategoryOptions)[number]) => {
	return reportTypeOptions.filter((type) => type.startsWith(`${category}-`));
};

export const getReportRow = (
	reportEvent: ReportEvent,
	reportEventColumnsList: (keyof ReportEventColumns)[],
	{ friendlyDisplay = true }: { friendlyDisplay?: boolean } = {},
	timezone = getDefaultTimezone(),
) => {
	const validRow = _.pick(reportEvent, reportEventColumnsList);
	// DEV NOTE::this also exists in the `scheduled-reports` endpoint - the two need to be updated in tandem
	const validateRowCell = (value: ReportEvent[keyof ReportEvent], key: keyof ReportEvent) => {
		if (!(typeof value === 'string' || typeof value === 'number')) {
			console.error(`Unexpected value for ${key} of type: ${typeof value}`);
			return '';
		}

		if (key === '_time') {
			if (friendlyDisplay) {
				return getLocalTime(value);
			}
			return getCSVReportTime(value, timezone);
		}

		if (friendlyDisplay && key === 'securityStatus') {
			// the untranslated security status exists on different property on `securityStatus`
			return <SecurityStatusIcon status={`${reportEvent.securityStatusKey}`} />;
		}
		return value;
	};
	const friendlyValidRow = _.mapValues(validRow, validateRowCell);
	return friendlyValidRow;
};

export const getReportGraphBackgroundColor = (index: number, reportType?: ReportType) => {
	let reportGraphBackgroundColors = [
		'#A7226E',
		'#5B3A82',
		'#007B88',
		'#9DE0AD',
		'#F47943',
		'#EC4899',
		'#7E22CE',
		'#2563EB',
		'#0D9488',
		'#65A30D',
		'#84CC16',
		'#FACC15',
		'#FB923C',
		'#DC2626',
		'#BE185D',
		'#831843',
		'#3730A3',
		'#0369A1',
		'#14B8A6',
		'#4ADE80',
		'#BEF264',
		'#FDE68A',
		'#FDBA74',
		'#C026D3',
		'#EF4444',
		'#F97316',
		'#F59E0B',
		'#10B981',
		'#06B6D4',
		'#6366F1',
		'#8B5CF6',
		'#D946EF',
	];
	if (reportType === 'bsn-device-events') {
		reportGraphBackgroundColors = ['#8EC43F', '#F72805', '#289BEE'];
	}
	return reportGraphBackgroundColors[index % reportGraphBackgroundColors.length];
};

const massageRawReportEvent = (rawReportEvent: any) => {
	const reportEvent: ReportEvent = {
		id: JSON.stringify(rawReportEvent),
		...rawReportEvent,
	};

	return reportEvent;
};

type ReportBarData = {
	label: string;
	value: number | number[];
};
const massageRawBar = (rawBar: any) => {
	const bar: ReportBarData = {
		label: rawBar.label,
		value: rawBar.value,
	};
	return bar;
};

type ReportAggregateData = string | number;
const massageRawAggregate = (rawAggregate: any) => {
	const aggregate: ReportAggregateData = typeof rawAggregate === 'number' ? Math.round(rawAggregate) : rawAggregate;
	return aggregate;
};

export type ReportEventColumns = {
	[key: string]: string;
};
const massageRawReportEventColumns = (rawReportEventColumns: any) => {
	const reportEventColumns: ReportEventColumns = {
		...rawReportEventColumns,
	};
	return reportEventColumns;
};

export const massageRawReport = (rawReport: any) => {
	const {
		data: {
			table: { head: rawTableEventColumns, rows: rawTableEvents },
			bars: rawBars,
			aggregate: rawAggregate,
		},
	} = rawReport;
	const tableEventColumns = massageRawReportEventColumns(rawTableEventColumns) as ReportEventColumns;
	const tableEvents = rawTableEvents.map(massageRawReportEvent) as ReportEvent[];
	const bars = rawBars.map(massageRawBar) as ReportBarData[];
	const aggregate = _.mapValues(rawAggregate, massageRawAggregate) as { [key: string]: ReportAggregateData };

	return {
		tableEventColumns,
		tableEvents,
		bars,
		aggregate,
	};
};
